/**
 * The generator's gate (docs/PATH-V2.md §3.5 p.20 «Правда — за Stockfish и кодом», §11.2): a generated task is served
 * only when every answer it ACCEPTS is good chess. The rules of the recipes (`saves`, `escapesCheck`, `safeCheck`,
 * `isSafe`, the fix of `teachme`) look one reply deep, so a review of round 2 found accepted answers that walk into a
 * fork by a check, a skewer, a mate in one, or leave another piece hanging (save@2 48/300, check@1 32/300, teachme@1
 * 22/300, safeCheck@1 6/300, safe@1 👍 2/300 confirmed by Stockfish) — and «Загадка дня» is always generated.
 *
 * The gate, per task:
 * - every accepted move (a move task's answers, the fixes of «Поправь меня», a 👍 «безопасно» move) keeps the
 *   mover's material: `moveValue` (the `isGift` search after the move — captures, promotions, quiet checks, full check
 *   evasions, mate decisive — carried on with captures) loses at most `GATE_MAX_LOSS` pawn, and never mate;
 * - a yes / no question is never about the only legal move (there is nothing to judge: «Безопасно?» 👍 on a forced
 *   move that is then mated).
 * Quiet threats (a pin, a fork without a check) stay unseen by the search — the engine-verified bank covers those.
 * A recipe gated here is a NEW version (`gatedRecipe`): the boards of its seeds are the base recipe's, minus the
 * refused ones (`generate` tries the next sub-seed); the base version stays registered for its released keys.
 */
import type { TaskSpec } from '@gambit/content';
import { MOVE_VALUE_MATE, moveValue } from '../../analysis/gifts.ts';
import { answerOf } from '../checks.ts';
import { legalMovesUci } from '../kingless.ts';
import type { Recipe } from './recipe.ts';

/** An accepted answer may lose at most this much material by `moveValue` (pawns: a pawn, never a piece). */
export const GATE_MAX_LOSS = 1;

/** Does the legal move `uci` keep the mover's material (≤ GATE_MAX_LOSS lost, no mate)? */
export function moveHolds(fen: string, uci: string): boolean {
  const v = moveValue(fen, uci);
  return v !== null && v > -MOVE_VALUE_MATE && v >= -GATE_MAX_LOSS;
}

/** Why the gate refuses `task` (null = it is served). */
export function gateProblem(task: TaskSpec): string | null {
  const a = answerOf(task);
  if ((a.kind === 'move' || a.kind === 'teachme') && (task.kind === 'move' || task.kind === 'teachme')) {
    for (const m of a.moves) if (!moveHolds(task.fen, m)) return `the accepted ${m} loses material`;
    return null;
  }
  if (task.kind === 'yesno' && a.kind === 'yesno') {
    // «он хочет забрать?» is about his move already on the board
    if (task.rule.rule === 'threat') return null;
    if (legalMovesUci(task.fen).length <= 1) return `${task.rule.move} is the only legal move`;
    if (task.rule.rule === 'isSafe' && a.answer === 'yes' && !moveHolds(task.fen, task.rule.move)) return `the 👍 ${task.rule.move} loses material`;
  }
  return null;
}

/** `base` behind the gate, as its new `version` (the same seeds, the same boards — the refused ones skipped). */
export function gatedRecipe(base: Recipe, version: number): Recipe {
  return {
    ...base,
    version,
    sample: (seed, tier) => {
      const task = base.sample(seed, tier);
      return task !== null && gateProblem(task) === null ? task : null;
    },
  };
}
